// A term's value scheme as a comparison reads it: every value in the scheme's
// order, as it comes back from a query over the cubes, with its label. A
// concept published as a term (`output: "iri"`) comes back as its full IRI, a
// typed one (`output: "typed"`, the 1–5 agreement scale) as its literal.

import { schemeConcepts, valueSchemeFor } from './mapping_workbench.js';

// A scheme that lists no values is an open code list (ISO country codes):
// any code may come back, the same code meaning the same thing on every side,
// so it can be lined up but has no order of its own. It may name `remainder`
// codes that stand for no value in particular (OTHER, unknown), which go last.
// null when the catalog gives the term no scheme.
export function termValues(catalog, term, expand) {
  const scheme = valueSchemeFor(catalog, term);
  if (!scheme) return null;
  const concepts = schemeConcepts(scheme);
  const value = (c) => ({ value: scheme.output === 'iri' ? expand(c.id) : c.id, code: c.id, label: c.label ?? c.id });
  return {
    id: scheme.id,
    output: scheme.output ?? null,
    scale: scheme.scale ?? null,
    open: !concepts.length,
    values: concepts.map(value),
    remainder: (scheme.remainder ?? []).map(value),
  };
}

// How a comparison can be drawn (docs/ref-belonging-plan.md, step 2):
// separate tables per side, lined up per answer, or along a two-ended scale.
export const VIEWS = { separate: 'Separate', aligned: 'Aligned', scale: 'Scale' };

// The views a comparison's data allows: Separate always; Aligned when its term
// has a value scheme, open or not, so a row is the same answer on every side; Scale when
// that scheme declares two opposite ends. `scheme` is termValues()'s result.
export function allowedViews(scheme) {
  return ['separate', ...(scheme ? ['aligned'] : []), ...(scheme?.scale ? ['scale'] : [])];
}

// What is wrong with a scheme's `scale` declaration, if it has one: every
// concept on exactly one side (low, middle, high), low and high not empty, and
// the sides in the scheme's own order, so the bar runs from one end to the
// other. The middle may be empty (an even scale).
export function scaleErrors(scheme) {
  if (!scheme?.scale) return [];
  const { low, middle = [], high } = scheme.scale;
  const at = `${scheme.id}.scale`;
  if (![low, middle, high].every(Array.isArray)) return [`${at}: low, middle and high must be lists`];
  const errors = [];
  if (!low.length || !high.length) errors.push(`${at}: low and high must not be empty`);
  const order = schemeConcepts(scheme).map((c) => c.id);
  const sides = [...low, ...middle, ...high];
  if (sides.join('|') !== order.join('|')) {
    errors.push(`${at}: low, middle and high must hold every concept once, in the scheme's order (${order.join(', ')})`);
  }
  return errors;
}
